import { Component, EventEmitter, HostListener, Input, OnInit, Output, AfterViewInit, OnDestroy, NgZone, ViewChildren, QueryList, ViewChild, ElementRef, SimpleChanges, OnChanges } from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { DynamicFieldConfig } from './dynamic-search-form.interface';
import { CommonModule } from '@angular/common';
import { DateMaskDirective } from '@app/shared/directives/dateMask.directive';
import { patternMatch } from '@app/shared/constants/validators.model';
import { InputFocusDirective } from '@app/shared/directives/input-focus.directive';
import { CalendarModule } from 'primeng/calendar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-dynamic-search-form',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, DateMaskDirective, InputFocusDirective, CalendarModule, MatTooltipModule, MatIconModule],
  templateUrl: './dynamic-search-form.component.html',
  styleUrls: ['./dynamic-search-form.component.scss']
})
export class DynamicSearchFormComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @ViewChildren(MatTooltip) tooltips!: QueryList<MatTooltip>;
  private removeScrollListener: (() => void) | null = null;
  private _chipRemoveClicked = false;
  todayString: string;
  maxDate: Date = new Date();
  @Input() config: DynamicFieldConfig[] = [];
  @Input() onReset: () => void;
  @Input() onSearch: (value: any) => void;
  @Input() buttonLabel: boolean = true;
  @Input() useValidSearchInput: boolean = false;
  @Input() showEmailValidation: boolean = false;
  @Input() disableSearchButton: boolean = false;

  @Output() resetEvent = new EventEmitter<void>();
  @Output() searchEvent = new EventEmitter<any>();
  @Output() fieldChange?: EventEmitter<any> = new EventEmitter<any>();
  @ViewChild('dateTextInput') dateTextInput?: ElementRef<HTMLInputElement>;
  @ViewChild('dateTextInputHidden') dateTextInputHidden?: ElementRef<HTMLInputElement>;

  form: FormGroup;
  chunkedConfig: DynamicFieldConfig[][] = [];
  dateMaskError: boolean = false;

  constructor(private fb: FormBuilder, private ngZone: NgZone) { }
  private noFutureDateValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }
      if (control.value.includes('*')) {
        return null;
      }

      let selectedDate: Date;

      if (control.value.includes('/')) {
        const parts = control.value.split('/');
        if (parts.length !== 3) {
          return { 'invalidDate': true };
        }
        const month = parseInt(parts[0], 10);
        const day = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);
        if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900) {
          return { 'invalidDate': true };
        }

        selectedDate = new Date(year, month - 1, day);
        if (selectedDate.getMonth() !== month - 1 ||
          selectedDate.getDate() !== day ||
          selectedDate.getFullYear() !== year) {
          return { 'invalidDate': true };
        }
      } else {
        selectedDate = new Date(control.value);
        if (isNaN(selectedDate.getTime())) {
          return { 'invalidDate': true };
        }
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      selectedDate.setHours(0, 0, 0, 0);

      if (selectedDate > today) {
        return { 'futureDate': true };
      }

      return null;
    };
  }

  private addValidatorForField(field: DynamicFieldConfig, v: string, validators: ValidatorFn[]) {
    if (v === 'required') {
      validators.push(Validators.required);
    }
    if (v === 'email') {
      validators.push(Validators.email);
      validators.push(Validators.maxLength(255));
    }
    if (field.fieldName === 'primary_phone' && v === 'pattern') {
      validators.push(Validators.pattern(/^\d{2,10}$/));
      validators.push(Validators.maxLength(10));
      validators.push(Validators.minLength(2));
    } else if (field.fieldName === 'ssn_number' && v === 'pattern') {
      validators.push(Validators.pattern(/^\d{4,9}$/));
      validators.push(Validators.maxLength(9));
      validators.push(Validators.minLength(4));
    } else if (v === 'pattern' && field.pattern) {
      let pattern = field.pattern;
      if (typeof pattern === 'string' && window['patternMatch'] && window['patternMatch'][pattern]) {
        pattern = window['patternMatch'][pattern];
      } else if (typeof pattern === 'string' && patternMatch[pattern]) {
        pattern = patternMatch[pattern];
      }
      validators.push(Validators.pattern(pattern));
    }
    if (field.fieldName !== 'primary_phone') {
      if (v === 'minlength' && typeof field.minLength === 'number') {
        validators.push(Validators.minLength(field.minLength));
      }
      if (v === 'maxlength' && typeof field.maxLength === 'number') {
        validators.push(Validators.maxLength(field.maxLength));
      }
    }
    if (v === 'datePattern' && window['patternMatch'] && window['patternMatch']['date']) {
      validators.push(Validators.pattern(window['patternMatch']['date']));
    } else if (v === 'datePattern' && patternMatch['date']) {
      validators.push(Validators.pattern(patternMatch['date']));
    }
  }

  ngOnInit() {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    this.todayString = `${yyyy}-${mm}-${dd}`;
    if (!window['patternMatch']) {
      window['patternMatch'] = {};
    }
    if (!window['patternMatch']['email10']) {
      window['patternMatch']['email10'] = patternMatch.email10;
    }
    const group: { [key: string]: any } = {};
    this.config.forEach(field => {
      const validators: ValidatorFn[] = [];
      if (field.validation) {
        field.validation.forEach(v => {
          this.addValidatorForField(field, v, validators);
        });
      }
      if (field.datatype === 'date' && field.noFutureDate) {
        validators.push(this.noFutureDateValidator());
      }
      let initialValue = field.value;
      if (field.datatype === 'dropdown' && (initialValue === undefined || initialValue === null || initialValue === '')) {
        initialValue = null;
      }
      if (field.datatype === 'daterange') {
        group[field.fieldName] = [{ value: null, disabled: field.disabled }, []];
      } else {
        group[field.fieldName] = [{ value: initialValue, disabled: field.disabled }, validators];
      }
    });
    this.form = this.fb.group(group);
    this.config.forEach(field => {
      if (field.disabled) {
        this.form.get(field.fieldName)?.disable({ emitEvent: false });
      } else {
        this.form.get(field.fieldName)?.enable({ emitEvent: false });
      }
    });
    this.chunkedConfig = this.chunkArray(this.config, 4);

    this.form.valueChanges.subscribe(() => {
      const phoneControl = this.form.get('primary_phone');
      if (phoneControl && phoneControl.value && phoneControl.value.length > 10) {
        phoneControl.setValue(phoneControl.value.slice(0, 10), { emitEvent: false });
      }
      if (this.formTouched && (this.form.valid || this.hasValidSearchInput())) {
        this.disableSearchButton = false;
      } else {
        this.disableSearchButton = true;
      }
    });

  }

  reset() {
    this.form.reset();
    this.config.forEach(field => {
      if (field.datatype === 'dropdown') {
        const control = this.form.get(field.fieldName);
        if (control) {
          control.setValue('');
        }
      }
      if (field.datatype === 'multiselectdropdown') {
        const control = this.form.get(field.fieldName);
        if (control) {
          control.setValue([]);
        }
        field['_open'] = false;
        field['_searchText'] = '';
      }
    });
    if (this.dateTextInput?.nativeElement) {
      this.dateTextInput.nativeElement.value = '';
    }
    if (this.dateTextInputHidden?.nativeElement) {
      this.dateTextInputHidden.nativeElement.value = '';
    }
    this.resetEvent.emit();
  }

  search() {
    const hasSearchableValue = this.hasValidSearchInput();
    if (this.form.valid || hasSearchableValue) {
      if (this.onSearch) {
        this.onSearch(this.form.value);
      }
      this.searchEvent.emit(this.form.value);
    }
  }

  private hasValidSearchInput(): boolean {
    if (!this.formTouched) return false;
    const values = this.form.value;
    for (const key of Object.keys(values)) {
      const val = values[key];
      if (val !== undefined && val !== null && val !== '') {
        if (typeof val === 'string') {
          const trimmed = val.trim();
          if (trimmed.includes('*') || trimmed.length >= 3 || /^\d{1,2}\//.test(trimmed) || /^\d{4}$/.test(trimmed)) {
            return true;
          }
        } else if (Array.isArray(val)) {
          if (val.length >= 2 && (val[0] || val[1])) {
            return true;
          }
        } else {
          return true;
        }
      }
    }
    return false;
  }

  private hasDateValidationErrors(): boolean {
    for (const field of this.config) {
      if (field.datatype === 'date' && field.noFutureDate) {
        const control = this.form.get(field.fieldName);
        if (control?.errors?.['futureDate'] || control?.errors?.['invalidDate']) {
          return true;
        }
      }
    }
    return false;
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config'] && !changes['config'].firstChange) {
      this.rebuildForm();
    }
  }

  private rebuildForm() {
    const currentValues = this.form ? this.form.getRawValue() : {};
    const group: { [key: string]: any } = {};
    this.config.forEach(field => {
      const validators: ValidatorFn[] = [];
      if (field.validation) {
        field.validation.forEach(v => {
          this.addValidatorForField(field, v, validators);
        });
      }
      if (field.datatype === 'date' && field.noFutureDate) {
        validators.push(this.noFutureDateValidator());
      }
      let initialValue = (currentValues && currentValues[field.fieldName] !== undefined)
        ? currentValues[field.fieldName]
        : field.value;
      if (field.datatype === 'dropdown' && (initialValue === undefined || initialValue === null || initialValue === '')) {
        initialValue = null;
      }
      if (field.datatype === 'multiselectdropdown') {
        initialValue = Array.isArray(initialValue) ? initialValue : [];
      }
      if (field.datatype === 'daterange') {
        group[field.fieldName] = [{ value: initialValue ?? null, disabled: field.disabled }, []];
      } else {
        group[field.fieldName] = [{ value: initialValue, disabled: field.disabled }, validators];
      }
    });
    this.form = this.fb.group(group);
    this.chunkedConfig = this.chunkArray(this.config, 4);
  }
  isSearch(): boolean {
    const ssnControl = this.form.get('ssn_number') || this.form.get('ssn');
    if (ssnControl && ssnControl.value) {
      const digits = ssnControl.value.replace(/\D/g, '');
      if (digits.length > 0 && digits.length < 4) {
        return true;
      }
    }

    if (this.useValidSearchInput) {
      for (const key in this.form.controls) {
        const control = this.form.get(key);
        if (control && typeof control.value === 'string' && control.value.includes('*')) {
          return false;
        }
      }
      const ssnControl = this.form.get('ssn_number');
      if (ssnControl && ssnControl.value && typeof ssnControl.value === 'string' && !ssnControl.value.includes('*')) {
        const digits = ssnControl.value.replace(/\D/g, '');
        if (digits.length > 0 && digits.length < 4) {
          return true;
        }
      }
      const emailControl = this.form.get('primary_email');
      const phoneControl = this.form.get('primary_phone');

      if (emailControl && emailControl.value && emailControl.invalid) {
        return true;
      }
      if (phoneControl && phoneControl.value && phoneControl.invalid) {
        return true;
      }
      if (!this.hasDateValidationErrors() && !this.hasSSNMinLengthError() && this.hasValidSearchInput()) {
        return false;
      }
      return true;
    }
    return this.form.invalid || !this.formTouched;
  }

  private hasSSNMinLengthError(): boolean {
    const ssnControl = this.form.get('ssn_number');
    if (ssnControl && ssnControl.value) {
      const ssnValue = ssnControl.value.replace(/\D/g, '');
      const ssnField = this.config.find(f => f.fieldName === 'ssn_number');
      const minLength = ssnField?.minLength || 4;
      if (ssnValue.length > 0 && ssnValue.length < minLength) {
        return true;
      }
    }
    return false;
  }

  private chunkArray<T>(arr: T[], size: number): T[][] {
    const result: T[][] = [];
    for (let i = 0; i < arr.length; i += size) {
      result.push(arr.slice(i, i + size));
    }
    return result;
  }

  onDateIconSelect(dateValue: string, dateTextInput: HTMLInputElement, controlName: string) {
    // Check if the field is disabled
    const control = this.form.get(controlName);
    if (control?.disabled) {
      return;
    }

    if (dateValue) {
      const d = new Date(dateValue);
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const yyyy = d.getFullYear();
      const formatted = `${mm}/${dd}/${yyyy}`;
      if (controlName && this.form && this.form.get(controlName)) {
        this.form.get(controlName)?.setValue(formatted);
      }
      setTimeout(() => {
        dateTextInput.value = formatted;
      }, 0);
      const event = new Event('input', { bubbles: true });
      dateTextInput.dispatchEvent(event);
      this.dateMaskError = false;
    } else {
      controlName && this.form && this.form.get(controlName)?.setValue('');
    }
  }

  onlyNumbers(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (
      charCode === 42 ||
      (charCode > 47 && charCode < 58)
    ) {
      return true;
    }
    event.preventDefault();
    return false;
  }

  get formTouched(): boolean {
    const values = this.form.value;
    return Object.keys(values).some(key => {
      const val = values[key];
      if (val === undefined || val === null || val === '') return false;
      if (Array.isArray(val)) return val.some(v => v !== undefined && v !== null && v !== '');
      return true;
    });
  }

  hasInfoTooltip(row: DynamicFieldConfig[]): boolean {
    return row.some(field => field.showInfoTooltip);
  }
  ngAfterViewInit() {
    this.ngZone.runOutsideAngular(() => {
      const handler = () => {
        this.ngZone.run(() => {
          if (this.tooltips) {
            this.tooltips.forEach(t => t.hide());
          }
        });
      };
      window.addEventListener('scroll', handler, true);
      this.removeScrollListener = () => window.removeEventListener('scroll', handler, true);
    });
  }
  clearDropdown(field: any) {
    this.form.get(field.fieldName)?.setValue(null);
    if (field.fieldName === 'license_kind') {
      this.fieldChange?.emit({ fieldName: 'license_kind', value: '' });
    }
  }

  clearDateRange(field: any) {
    this.form.get(field.fieldName)?.setValue(null);
    // Optionally emit fieldChange or searchEvent if needed
    this.fieldChange?.emit({ fieldName: field.fieldName, value: null });
  }
 onDropdownChange(field: any, event: Event) {
    const value = (event.target as HTMLSelectElement).value;
    this.form.get(field.fieldName)?.setValue(value);
    // Only emit searchEvent for fields that are NOT license_kind or endorsement_type
    if (field.fieldName !== 'license_kind' && field.fieldName !== 'endorsement_type') {
      this.searchEvent.emit({ fieldName: field.fieldName, value });
    }
    // Always emit fieldChange
    this.fieldChange?.emit({ fieldName: field.fieldName, value });
  }

  onMultiselectWrapperClick(field: DynamicFieldConfig, event: Event) {
    const target = event.target as HTMLElement;
    // Ignore clicks on chip remove, clear button, search input, checkboxes, or dropdown panel
    if (target.closest('.multiselect-chip-remove') ||
        target.closest('.multiselect-clear') ||
        target.closest('.multiselect-dropdown-panel')) {
      return;
    }
    // Only toggle if clicked on the select box area
    if (target.closest('.multiselect-select-box')) {
      this.toggleMultiselect(field);
    }
  }

  // --- Multiselect dropdown methods ---
  toggleMultiselect(field: DynamicFieldConfig) {
    if (field.disabled) return;
    field['_open'] = !field['_open'];
    if (!field['_open']) {
      field['_searchText'] = '';
    }
  }

  closeAllMultiselects() {
    this.config.forEach(f => {
      if (f.datatype === 'multiselectdropdown') {
        f['_open'] = false;
        f['_searchText'] = '';
      }
    });
  }

  onMultiselectSearch(field: DynamicFieldConfig, event: Event) {
    field['_searchText'] = (event.target as HTMLInputElement).value;
  }

  getFilteredMultiselectOptions(field: DynamicFieldConfig): { label: string; value: any }[] {
    const options = field.options || [];
    const search = (field['_searchText'] || '').toLowerCase().trim();
    if (!search) return options;
    return options.filter(opt => opt.label.toLowerCase().includes(search));
  }

  isMultiselectOptionSelected(field: DynamicFieldConfig, value: any): boolean {
    const selected: any[] = this.form.get(field.fieldName)?.value || [];
    return selected.includes(value);
  }

  onMultiselectOptionToggle(field: DynamicFieldConfig, value: any, event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    let selected: any[] = [...(this.form.get(field.fieldName)?.value || [])];
    if (checked) {
      if (!selected.includes(value)) selected.push(value);
    } else {
      selected = selected.filter(v => v !== value);
    }
    this.form.get(field.fieldName)?.setValue(selected);
    this.form.get(field.fieldName)?.markAsTouched();
    this.fieldChange?.emit({ fieldName: field.fieldName, value: selected });
  }

  getMultiselectDisplay(field: DynamicFieldConfig): string {
    const selected: any[] = this.form.get(field.fieldName)?.value || [];
    if (!selected.length) return '';
    const options = field.options || [];
    const labels = selected.map(v => options.find(o => o.value === v)?.label || v);
    if (labels.length <= 2) return labels.join(', ');
    return `${labels[0]}, ${labels[1]} +${labels.length - 2} more`;
  }

  getSelectedChips(field: DynamicFieldConfig): { label: string; value: any }[] {
    const selected: any[] = this.form.get(field.fieldName)?.value || [];
    const options = field.options || [];
    return selected.map(v => ({ label: options.find(o => o.value === v)?.label || v, value: v }));
  }

  getChipLabel(field: DynamicFieldConfig, value: any): string {
    const options = field.options || [];
    return options.find(o => String(o.value) === String(value))?.label || value;
  }

  getChipDisplayLabel(field: DynamicFieldConfig, value: any): string {
    const label = this.getChipLabel(field, value);
    return label.length > 20 ? `${label.slice(0, 20)}...` : label;
  }

  needsTooltip(text: string): boolean {
    return !!(text && text.length > 19);
  }

  removeChip(field: DynamicFieldConfig, chipValue: any, event: Event) {
    event.stopPropagation();
    const current: any[] = this.form.get(field.fieldName)?.value || [];
    const updated = current.filter(v => String(v) !== String(chipValue));
    this.form.get(field.fieldName)?.setValue(updated);
    this.form.get(field.fieldName)?.markAsTouched();
    this.fieldChange?.emit({ fieldName: field.fieldName, value: updated });
  }

  clearMultiselect(field: DynamicFieldConfig) {
    this.form.get(field.fieldName)?.setValue([]);
    field['_open'] = false;
    field['_searchText'] = '';
    this.fieldChange?.emit({ fieldName: field.fieldName, value: [] });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    const target = event.target as HTMLElement;
    if (!target.closest('.multiselect-wrapper')) {
      this.closeAllMultiselects();
    }
  }
  ngOnDestroy() {
    if (this.removeScrollListener) {
      this.removeScrollListener();
    }
  }
}
